import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import DashboardV2 from '~/components/DashboardV2/DashboardV2';
import { DashboardV3 } from '~/components/DashboardV3/DashboardV3';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { ErrorPage } from '~/eds/src/components/ErrorPage/ErrorPage';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { Dashboard } from '~/features/dashboard/Dashboard';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useDashboardsSubnavs } from '~/hooks/useDashboardsSubnavs';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { testIsAdmin } from '~/utils/user';

const Page = () => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const { navigate, params } = useRouting();
  const { dashboardId } = params;
  const { dashboards, isFetching } = useDashboardsSubnavs();
  const hasDashboardV3 = useFlag(FlagType.DashboardV3); // search service
  const hasDeprecatePilotSearch = useFlag(FlagType.DeprecatePilotSearch);
  const hasRBAC = useFlag(FlagType.NewUsersPage); // RBAC
  const { toast } = useToast();

  const isLegacyDashboard = useMemo(() => {
    // Only legacy dashboards have the type attribute.
    // Can be removed once we deprecate looker dashboars.
    return dashboards.find((dashboard) => dashboard.type === dashboardId);
  }, [dashboardId, dashboards]);

  const {
    data: dashboardResponse,
    error: errorFetchingDashboard,
    isFetching: isFetchingDashboard,
    isError: isErrorFetchingDashboard,
  } = api.endpoints.getDashboardV2.useQuery(dashboardId, {
    skip: !dashboards.length || !dashboardId || isLegacyDashboard,
  });

  const errorCode = errorFetchingDashboard?.response?.status;
  const isAdmin = testIsAdmin(currentUser);
  const isTicketMockedDashboard =
    dashboardId === 'open_tickets' || dashboardId === 'completed_tickets';

  useEffect(() => {
    if (!dashboardId && dashboards?.length && !isFetching) {
      const [firstDashboard] = dashboards;
      if (!!firstDashboard.creator_id) {
        navigate(`/dashboards/${firstDashboard.id}`, { replace: true });
      } else {
        navigate(`/dashboards/${firstDashboard.type}`, { replace: true });
      }
    }
  }, [dashboards, dashboardId, isFetching]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateBack = () => navigate(RoutePathType.Home);

  const getErrorToast = () => {
    const resp = errorFetchingDashboard?.response || {};
    const msg = resp.data?.errors?.[0];
    if (resp.status === HttpStatusCodeType.NotFound) {
      return intl.formatMessage({
        id: 'DashboardPage.dashboardNotFound',
        defaultMessage: 'Dashboard not found',
        description: 'Error message when dashboard is not found',
      });
    } else if (msg) {
      return msg.title;
    } else {
      return intl.formatMessage({
        id: 'DashboardPage.loadingError',
        defaultMessage: 'An error occurred while loading the dashboard.',
        description: 'Generic error message when dashboard fails to load',
      });
    }
  };
  useEffect(() => {
    if (isErrorFetchingDashboard) {
      if (errorCode !== HttpStatusCodeType.Forbidden) {
        toast({
          message: getErrorToast(),
          status: 'danger',
        });
      }
    }
  }, [isErrorFetchingDashboard]);

  const isPreSigDashboard =
    dashboardResponse?.entity === 'ticket' ||
    dashboardResponse?.entity === 'workflow';

  const renderDashboard = () => {
    // TODO: Remove once we deprecate mock data for pre-signature dashboards
    if (isTicketMockedDashboard && !hasRBAC && !isAdmin) {
      return (
        <ErrorPage
          preset="page-access"
          resolveAction={{ onClick: navigateBack }}
        />
      );
    }

    if (!isErrorFetchingDashboard && dashboardResponse) {
      if (hasDeprecatePilotSearch) {
        return (
          <Dashboard key={dashboardResponse.id} dashboard={dashboardResponse} />
        );
      } else {
        if (isPreSigDashboard && hasDashboardV3) {
          return <DashboardV3 dashboard={dashboardResponse} />;
        } else {
          return <DashboardV2 dashboard={dashboardResponse} />;
        }
      }
    }
    if (errorCode === HttpStatusCodeType.Forbidden) {
      return (
        <ErrorPage
          preset="page-access"
          resolveAction={{ onClick: navigateBack }}
        />
      );
    }
    return <EmptyPage preset="no-dashboard" />;
  };

  return (
    <FlexLayout flexDirection="column" space="x4">
      <ContentContainer
        loadingContent={{
          isLoading: isFetching || isFetchingDashboard,
          message: intl.formatMessage({
            id: 'DashboardPage.loadingMessage',
            defaultMessage: 'Loading Dashboards...',
            description: 'Loading message while dashboards are being fetched',
          }),
        }}
      >
        {renderDashboard()}
      </ContentContainer>
    </FlexLayout>
  );
};

export default Page;
